import { useParams, useNavigate, Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { ArrowLeft } from 'lucide-react'
import type { OfferPost, ActionPost } from '@olimhub/shared'
import { postsApi } from '@/api'
import { PostCard } from '@/components/post/PostCard'
import { ShareButton } from '@/components/post/ShareButton'
import { ActionManagePanel } from '@/components/post/ActionManagePanel'
import { Avatar } from '@/components/profile/Avatar'
import { useAuthStore } from '@/store/auth.store'

function pageTitle(post: Awaited<ReturnType<typeof postsApi.getById>>): string {
  if (post.type === 'event') return 'Event'
  if (post.type === 'action') return 'Action'
  if (post.type === 'offer') {
    const offer = (post as OfferPost).offer
    return offer.offerType === 'service' ? 'Help' : 'Bazaar'
  }
  return 'Post'
}

function PostSkeleton() {
  return (
    <div style={{ padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        <div style={{ width: 40, height: 40, borderRadius: '50%', background: 'var(--border)', flexShrink: 0 }} />
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ width: 120, height: 14, borderRadius: 4, background: 'var(--border)' }} />
          <div style={{ width: 80, height: 12, borderRadius: 4, background: 'var(--border)' }} />
        </div>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <div style={{ width: '100%', height: 14, borderRadius: 4, background: 'var(--border)' }} />
        <div style={{ width: '85%', height: 14, borderRadius: 4, background: 'var(--border)' }} />
        <div style={{ width: '60%', height: 14, borderRadius: 4, background: 'var(--border)' }} />
      </div>
    </div>
  )
}

export function PostDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { account: authAccount, isRestoring } = useAuthStore()

  // Wait for SilentRefresh to resolve before firing this query — otherwise, for a
  // logged-in viewer, it goes out anonymously (access token isn't in memory yet on
  // a fresh load) and comes back missing viewer-specific fields like the Action
  // RSVP button's state, with nothing forcing a re-fetch once auth catches up.
  const { data: post, isLoading, isError } = useQuery({
    queryKey: ['post', id],
    queryFn: () => postsApi.getById(id!),
    enabled: !!id && !isRestoring,
    retry: false,
  })

  const isAuthor = !!authAccount && !!post && post.account.id === authAccount.id

  const { data: clapsData } = useQuery({
    queryKey: ['post-claps', id],
    queryFn: () => postsApi.getClaps(id!),
    enabled: !!id && isAuthor,
  })

  const title = post ? pageTitle(post) : 'Post'

  return (
    <div style={{ maxWidth: 'var(--feed-max)', margin: '0 auto' }}>
      {/* Header */}
      <div
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          padding: '0 8px',
          height: 56,
          background: 'var(--surface)',
          borderBottom: '1px solid var(--border)',
        }}
      >
        <button
          type="button"
          aria-label="Go back"
          onClick={() => navigate(-1)}
          style={{
            width: 38,
            height: 38,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            border: 'none',
            background: 'none',
            cursor: 'pointer',
            color: 'var(--text-strong)',
            flexShrink: 0,
          }}
        >
          <ArrowLeft size={20} />
        </button>
        <h1
          style={{
            margin: 0,
            flex: 1,
            fontFamily: 'var(--font-sans)',
            fontWeight: 700,
            fontSize: 'var(--fs-body-lg)',
            color: 'var(--text-strong)',
          }}
        >
          {title}
        </h1>
        {post && (
          <ShareButton
            postId={post.id}
            authorUsername={post.account.username}
            contentPreview={post.content}
          />
        )}
      </div>

      {/* Body */}
      {isLoading || isRestoring ? (
        <div
          style={{
            background: 'var(--surface)',
            borderRadius: 'var(--radius-md)',
            boxShadow: 'var(--shadow-card)',
            margin: 8,
          }}
        >
          <PostSkeleton />
          <div style={{ padding: '8px 16px 16px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            <div style={{ width: '100%', height: 1, background: 'var(--border)' }} />
            {[0, 1].map((i) => (
              <div key={i} style={{ display: 'flex', gap: 10 }}>
                <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--border)', flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ width: '70%', height: 12, borderRadius: 4, background: 'var(--border)', marginBottom: 6 }} />
                  <div style={{ width: '90%', height: 12, borderRadius: 4, background: 'var(--border)' }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : isError || !post ? (
        <div
          style={{
            textAlign: 'center',
            padding: '64px 24px',
            fontFamily: 'var(--font-sans)',
            color: 'var(--text-muted)',
          }}
        >
          <p
            style={{
              fontSize: 'var(--fs-body-lg)',
              fontWeight: 700,
              color: 'var(--text-strong)',
              marginBottom: 8,
            }}
          >
            This post doesn't exist or was deleted.
          </p>
          <Link to="/" style={{ color: 'var(--text-link)', fontSize: 'var(--fs-sm)' }}>
            Back to home
          </Link>
        </div>
      ) : (
        <div style={{ margin: 8 }}>
          <PostCard
            post={post as Parameters<typeof PostCard>[0]['post']}
            defaultRepliesOpen
            isDetailPage
          />
          {post.type === 'action' && <ActionManagePanel post={post as ActionPost} />}
          {isAuthor && clapsData && clapsData.items.length > 0 && (
            <div
              style={{
                background: 'var(--surface)',
                borderTop: '1px solid var(--border)',
                padding: '10px 16px',
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{ fontSize: 16 }}>👏</span>
              {clapsData.items.map((entry) => (
                <Link
                  key={entry.accountId}
                  to={`/@${entry.account.username}`}
                  title={entry.account.displayName || entry.account.username}
                  style={{ textDecoration: 'none', flexShrink: 0 }}
                >
                  <Avatar
                    name={entry.account.displayName || entry.account.username}
                    avatarUrl={entry.account.avatarUrl}
                    animalAvatar={entry.account.animalAvatar}
                    size="sm"
                  />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}
